/**
 * Builds inclusive UTC calendar-day bounds for Time-on-Task filters.
 * Uses the local calendar Y/M/D of the given Date (as produced by the date picker),
 * not moment.utc(instant), so US timezones do not drop the start day.
 */

/** Preset day chips shared by Outcomes / learner DaysFilter. */
export const TOT_PRESET_DAY_COUNTS = [7, 30, 90, 365];

export const isTotPresetDayCount = value => TOT_PRESET_DAY_COUNTS.includes(value);

/**
 * Legacy rolling window used by learner DaysFilter (matches historical getRangeData).
 * @param {number} days
 * @param {Date} [now]
 * @returns {{ startDate: string, endDate: string }}
 */
export function toLegacyRollingPresetRange(days, now = new Date()) {
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
}

/**
 * Parse a stored filter instant into a local calendar Date for DateRangePicker.
 * Uses UTC Y/M/D so inclusive bounds like 2026-05-01T00:00:00.000Z map to May 1 locally.
 * @param {Date|string|number} value
 * @returns {Date|null}
 */
export function toLocalCalendarDateFromFilterInstant(value) {
  if (value == null || value === '') {
    return null;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Build materialui-daterange-picker `initialDateRange` from global filter range.
 * @param {{ startDate?: Date|string|number, endDate?: Date|string|number }|null|undefined} range
 * @returns {{ startDate: Date, endDate: Date }|undefined}
 */
export function toDateRangePickerInitial(range) {
  if (!range) {
    return undefined;
  }
  const startDate = toLocalCalendarDateFromFilterInstant(range.startDate);
  const endDate = toLocalCalendarDateFromFilterInstant(range.endDate);
  if (!startDate || !endDate) {
    return undefined;
  }
  return { startDate, endDate };
}

const asLocalYmd = date => {
  const d = date instanceof Date ? date : new Date(date);
  return {
    y: d.getFullYear(),
    m: d.getMonth(),
    day: d.getDate()
  };
};

const utcDayStartIso = ({ y, m, day }) =>
  new Date(Date.UTC(y, m, day, 0, 0, 0, 0)).toISOString();

const utcDayEndIso = ({ y, m, day }) =>
  new Date(Date.UTC(y, m, day, 23, 59, 59, 999)).toISOString();

/**
 * @param {Date|string|number} startDate
 * @param {Date|string|number} endDate
 * @returns {{ startDate: string, endDate: string }}
 */
export function toUtcInclusiveDayRange(startDate, endDate) {
  const start = asLocalYmd(startDate);
  const end = asLocalYmd(endDate);
  return {
    startDate: utcDayStartIso(start),
    endDate: utcDayEndIso(end)
  };
}

/**
 * Last N calendar days inclusive of today (local calendar), as UTC day bounds.
 * @param {number} days
 * @param {Date} [now]
 * @returns {{ startDate: string, endDate: string }}
 */
export function toUtcInclusivePresetRange(days, now = new Date()) {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return toUtcInclusiveDayRange(start, end);
}

/**
 * Inclusive calendar-day count between two local dates (e.g. Jul 1–7 => 7).
 * @param {Date|string|number} startDate
 * @param {Date|string|number} endDate
 * @returns {number}
 */
export function calendarDaysInclusive(startDate, endDate) {
  const s = asLocalYmd(startDate);
  const e = asLocalYmd(endDate);
  const startUtc = Date.UTC(s.y, s.m, s.day);
  const endUtc = Date.UTC(e.y, e.m, e.day);
  return Math.floor((endUtc - startUtc) / 86400000) + 1;
}

/**
 * Resolve custom picker range for gettimeontask when inclusive FF is on.
 * @param {{ startDate: Date|string|number, endDate: Date|string|number }} range
 * @returns {{ formattedRange: { startDate: string, endDate: string }, daysCount: number }}
 */
export function resolveInclusiveCustomFilterDateRange(range) {
  return {
    formattedRange: toUtcInclusiveDayRange(range.startDate, range.endDate),
    daysCount: calendarDaysInclusive(range.startDate, range.endDate)
  };
}
